// Rollback (szzt-spec.md §8): repoint `site.current` to a prior deployment via a guarded
// update of the `app.szzt.site` record ONLY — no blob movement, no rebuild. The target deployment,
// its manifest record, and its blobs already exist and are untouched.
//
// Unlike publish (a multi-record atomic write that can only use `swapCommit`), rollback touches a
// single record, so it can use true per-record `swapRecord` CAS — the exact guard §7.1/§8 describe.
// It ALSO guards the repo head. `swapRecord` alone only asks "did anyone else move the pointer?",
// and the write that breaks us moves no pointer: `prune` (§16) deletes a deployment guarded by
// `swapCommit` on the head and never touches the site record, so a `swapRecord`-only rollback still
// matches and swaps `current` onto deleted bytes — the site 404s at every gateway. The two guards
// are complementary and both are needed: `swapCommit` alone would not notice a prune that landed
// BEFORE we read (the head we capture is already the post-prune one), and the target check alone is
// TOCTOU (prune can land between the check and the swap). Together — head captured FIRST, then the
// reads it protects, then the swap — a prune either loses the swap or is seen by the target check.
import { parseAtUri, readStrongRef } from "@szzt/records";
import { COLLECTION } from "./records.ts";
import {
  type PdsClient,
  type RollbackInput,
  type RollbackResult,
  type SiteRecord,
  type StrongRef,
  SwapFailedError,
} from "./types.ts";

/** Thrown when the deployment `target` names is gone, half-pruned, or no longer the pinned CID (§8). */
export class RollbackTargetMissingError extends Error {
  constructor(
    readonly targetUri: string,
    readonly reason: string,
  ) {
    super(`refusing to roll back onto a target that is not intact: ${targetUri} — ${reason}`);
    this.name = "RollbackTargetMissingError";
  }
}

/**
 * Check that `target` is a deployment we can actually serve: the `app.szzt.deployment` record exists
 * under EXACTLY the pinned `target.cid`, and its `tile` manifest record likewise. Returns a reason
 * string on any problem, or `null` when the target is intact.
 *
 * Both halves matter. CID rather than mere existence: a record that exists at the URI but under a
 * different CID is a different deployment, and `site.current.cid` is a strong ref a gateway
 * re-derives and fails closed on (§9.2). And the manifest as well as the deployment: `planPrune`
 * deletes BOTH records per deployment in one `applyWrites`, but a partial repo (a half-landed or
 * hand-made delete) can leave the deployment record pointing at a manifest that is gone.
 */
async function targetProblem(client: PdsClient, target: StrongRef): Promise<string | null> {
  const { collection, rkey } = parseAtUri(target.uri);
  const deployment = await client.getRecord(collection, rkey);
  if (deployment === null) return "deployment record does not exist (pruned?)";
  if (deployment.cid !== target.cid) {
    return `deployment record cid=${deployment.cid} != pinned target cid=${target.cid}`;
  }

  const tile = readStrongRef(deployment.value, "tile", COLLECTION.deployment);
  const man = parseAtUri(tile.uri);
  const manifest = await client.getRecord(man.collection, man.rkey);
  if (manifest === null) {
    return `manifest record ${tile.uri} does not exist (half-pruned deployment?)`;
  }
  if (manifest.cid !== tile.cid) {
    return `manifest record ${tile.uri} cid=${manifest.cid} != deployment tile.cid=${tile.cid}`;
  }
  return null;
}

/**
 * Repoint a site's active version to `target` (a prior deployment strong ref, §8). Preserves
 * every other field on the `app.szzt.site` record. Guarded by both the site record's current CID and
 * the repo head (see this file's header for why both); on a concurrent-write conflict it re-reads,
 * re-checks the target, and retries.
 *
 * Throws {@link RollbackTargetMissingError} — with ZERO writes — when `target` no longer resolves to
 * an intact deployment, rather than swapping `current` onto bytes a gateway cannot resolve.
 */
export async function rollback(input: RollbackInput): Promise<RollbackResult> {
  const { client, siteRkey, target, maxRetries = 3 } = input;

  for (let attempt = 0; ; attempt++) {
    // Head FIRST — before the reads it protects. Anything that lands after this point (a prune of
    // `target`, a concurrent publish) invalidates the swap rather than slipping between our checks
    // and our write. Same ordering, and the same reason, as `publish.ts rollbackFailedPublish()`.
    const head = await client.getLatestCommit();

    const existing = await client.getRecord(COLLECTION.site, siteRkey);
    if (existing === null) {
      throw new Error(`cannot roll back: no ${COLLECTION.site} record at rkey ${siteRkey}`);
    }
    const value = existing.value as SiteRecord;

    const problem = await targetProblem(client, target);
    if (problem !== null) throw new RollbackTargetMissingError(target.uri, problem);

    const next: SiteRecord = { ...value, current: target };

    try {
      const res = await client.putRecord({
        collection: COLLECTION.site,
        rkey: siteRkey,
        value: next,
        swapRecord: existing.cid,
        swapCommit: head.cid,
      });
      return { siteUri: res.uri, siteCid: res.cid, current: target };
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      throw err;
    }
  }
}
